/**
 * Date Helper Utilities
 * 
 * Queste funzioni risolvono i problemi di fuso orario causati dal parsing 
 * delle stringhe ISO date-only (YYYY-MM-DD) che JavaScript interpreta come UTC midnight.
 */

/**
 * Parsa una stringa di data in formato YYYY-MM-DD come data locale (non UTC)
 * 
 * PROBLEMA: new Date("2025-01-10") viene interpretato come UTC 00:00:00
 * - Per utenti in Brasile (UTC-3): diventa 2025-01-09 21:00:00 locale (giorno sbagliato!)
 * 
 * SOLUZIONE: Aggiunge 'T00:00:00' per forzare interpretazione come orario locale
 * 
 * @param {string} dateString - Data in formato YYYY-MM-DD (es: "2025-01-10")
 * @returns {Date} Oggetto Date con la data corretta nel fuso orario locale
 * 
 * @example
 * // Utente in Brasile (UTC-3)
 * const wrong = new Date("2025-01-10");        // 2025-01-09 21:00:00 ❌
 * const correct = parseLocalDate("2025-01-10"); // 2025-01-10 00:00:00 ✅
 */
export const parseLocalDate = (dateString) => {
  if (!dateString) return new Date();
  
  // Se la stringa contiene già l'orario, usala così com'è
  if (dateString.includes('T') || dateString.includes(' ')) {
    return new Date(dateString);
  }
  
  // Aggiungi T00:00:00 per forzare interpretazione locale
  return new Date(dateString + 'T00:00:00');
};

/**
 * Formatta una data in formato YYYY-MM-DD (ISO date-only)
 * 
 * @param {Date} date - Oggetto Date da formattare
 * @returns {string} Data formattata come YYYY-MM-DD
 * 
 * @example
 * const date = new Date(2025, 0, 10); // 10 gennaio 2025
 * formatDateYYYYMMDD(date); // "2025-01-10"
 */
export const formatDateYYYYMMDD = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formatta una data in formato DD/MM/YYYY
 * 
 * @param {Date|string} date - Data da formattare
 * @returns {string} Data formattata come DD/MM/YYYY
 * 
 * @example
 * formatDateDDMMYYYY("2025-01-10"); // "10/01/2025"
 */
export const formatDateDDMMYYYY = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : parseLocalDate(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${day}/${month}/${year}`;
};

/**
 * Normalizza una data ignorando l'orario (solo giorno/mese/anno)
 * 
 * @param {Date|string} date - Data da normalizzare
 * @returns {Date} Data normalizzata a mezzanotte locale
 * 
 * @example
 * const d1 = new Date("2025-01-10T14:30:00");
 * const d2 = normalizeDate(d1);
 * // d2 sarà 2025-01-10 00:00:00
 */
export const normalizeDate = (date) => {
  const d = date instanceof Date ? date : parseLocalDate(date);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/** True if dateStr (YYYY-MM-DD) is strictly after today (local). */
export const isFutureDay = (dateStr) => {
  const d = parseLocalDate(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d > today;
};
